import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { InlineWidget } from 'react-calendly';
import { TFunction, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Typography, Box } from '@material-ui/core';
import { ClassNameMap } from '@material-ui/styles';
import { useQuery } from '@tanstack/react-query';
import { getBookings } from '../../../services';
import { AuthContext } from '../../shared/app/AuthProvider';
import { IBookingEvent } from '../../../types';
import {
  fetchBookingsHistory,
  selectFilters,
  selectUpcomingEvents,
  selectUser,
} from '../../../store';
import { useStyles } from './styles';
import { MentorProfileProps } from './interface';
import VideoJS from '../../shared/videoPlayer';
import ExpansionPanel from '../../shared/expansionPanel';
import AvatarItem from '../../shared/avatarItem';
import { ReactComponent as EducationSVG } from './assets/education.svg';
import MentorTags from '../mentor-card/MentorTags';
import { addMentor, addToastInfo } from '../../../store/common';
import OfferingCard from '../offering-card';
import useSearchParams from '../../shared/hook/useSearchParams';
import DrawerHeader from '../../shared/drawer-header';
import useGetEvents from '../../shared/hook/use-get-events';
import ChipList from '../../shared/chip-list';

const careerEducationTitle = (t: TFunction, classes: ClassNameMap) => {
  return (
    <Box className={classes.educationHeader}>
      <EducationSVG />
      <Typography className={classes.expansionPanelTitle}>
        {t('v2.global.label_careerAndEducation')}
      </Typography>
    </Box>
  );
};

const MentorProfile: FC<MentorProfileProps> = ({
  mentor,
  onClose,
  scrolled,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { authUser } = React.useContext(AuthContext);
  const { user } = useSelector(selectUser);
  const [mentorEvents, setMentorEvents] = React.useState<IBookingEvent[]>([]);
  const { upcomingEvents } = useSelector(selectUpcomingEvents);
  const { recommendationId } = useGetEvents();
  const filters = useSelector(selectFilters);
  const { refetch: refetchMyBookings } = useQuery(['my-bookings'], getBookings);

  const searchparams = useSearchParams();
  const utmCampaign = searchparams.get('utm_campaign') ?? 'WEBAPP';

  const getCalendlyURL = (url?: string) =>
    `${url}?name=${authUser.attributes.given_name}&email=${encodeURIComponent(
      authUser.attributes.email,
    )}`;

  useEffect(() => {
    setMentorEvents(
      upcomingEvents?.filter(
        (event: IBookingEvent) => event.mentor?.id === mentor.id,
      ),
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upcomingEvents]);

  const isCalendlyScheduledEvent = (e: any) => {
    return (
      e.data.event &&
      e.data.event.indexOf('calendly') === 0 &&
      e.data.event === 'calendly.event_scheduled'
    );
  };

  useEffect(() => {
    const onMessage = (e: any) => {
      if (isCalendlyScheduledEvent(e) && user) {
        onClose();

        refetchMyBookings();
        dispatch(fetchBookingsHistory());
        dispatch(
          addToastInfo({
            toastInfo: {
              open: true,
              type: 'success',
              content: t('v2.global.snackbar_label_successfulBooking'),
            },
          }),
        );
        const calendlyInviteeUri = e.data.payload.invitee.uri ?? '';
        history.push({
          pathname: '/assessment',
          state: { calendlyInviteeUri },
        });
      }
    };

    window.addEventListener('message', onMessage);

    return () => window.removeEventListener('message', onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onEvent = (offering: IBookingEvent) => {
    history.replace({
      pathname: window.location.pathname,
      search: `offeringId=${offering.id.toString()}`,
      previous: window.location.search,
    });

    dispatch(addMentor({}));
  };

  const topicList = mentor.details?.focusTopics?.map((item) =>
    t(`v2.global.chip_label_topic_${item}`),
  );

  return (
    <>
      <DrawerHeader
        items={[
          {
            component: t('v2.global.label_recommendedForYou'),
            className: classes.recommended,
            show: Boolean(mentor.isRecommended),
          },
        ]}
        scrolled={scrolled}
      />
      <Box className={classes.profileContainer}>
        <Box className={classes.headerContainer}>
          <Box className={classes.mentorWrapper}>
            {mentor.details?.profileImage && (
              <img
                alt="Mentor"
                className={classes.mentorImage}
                src={mentor.details.profileImage}
              />
            )}
            <Box className={classes.mentorTitleSection}>
              <Typography id="label_mentor_name" variant="h3">
                {mentor.name}
              </Typography>
              <Typography variant="body1" className={classes.subtitle}>
                {mentor.details?.education}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box className={classes.sectionContainer}>
          <Typography className={classes.specializedLabel} variant="overline">
            {t('v2.global.label_specialisedIn')}
          </Typography>

          <ChipList list={topicList} />

          <Typography variant="caption">
            {t('v2.global.label_canSupportOtherTopics')}
          </Typography>
        </Box>

        {mentor.details?.approach && (
          <Box className={classes.sectionContainer}>
            <Typography className={classes.specializedLabel} variant="overline">
              {t('v2.global.label_approach')}
            </Typography>
            <ChipList list={[mentor.details?.approach]} />
          </Box>
        )}
        <Box className={classes.languageWrapper}>
          <MentorTags mentor={mentor} />
        </Box>
        <Box className={classes.descriptionWrapper}>
          <Typography component="p" className={classes.description}>
            {mentor.details?.description}
          </Typography>

          {mentor.details?.videoUrl && (
            <Box className={classes.videoContainer}>
              <VideoJS
                url={mentor.details?.videoUrl}
                width={276}
                height={140}
              />
            </Box>
          )}
          <ExpansionPanel
            title={careerEducationTitle(t, classes)}
            content={
              <Box>
                {(mentor.details?.work?.length ?? 0) > 0 && (
                  <Typography className={classes.educationSubtitle}>
                    {t('v2.global.label_workExperience')}
                  </Typography>
                )}
                {mentor.details?.work?.map((item, index) => (
                  <>
                    <AvatarItem header={item.title} subHeader={item.subtitle} />
                    {index < mentor.details!.work!.length - 1 && (
                      <div className={classes.line} />
                    )}
                  </>
                ))}
                {(mentor.details?.educationBackground?.length ?? 0) > 0 && (
                  <Typography className={classes.educationSubtitle}>
                    {t('v2.global.label_university')}
                  </Typography>
                )}
                {mentor.details?.educationBackground?.map((item, index) => (
                  <>
                    <AvatarItem
                      variant="blue"
                      header={item.title}
                      subHeader={item.subtitle}
                    />
                    {index <
                      mentor.details!.educationBackground!.length - 1 && (
                      <div className={classes.line} />
                    )}
                  </>
                ))}
              </Box>
            }
          />
          <Typography className={classes.descriptionTitle}>
            {t(
              'v2.mentorOverview.drawer_mentorDetails_title_availability1-on1',
            )}
          </Typography>
          <Typography>
            {t(
              'v2.mentorOverview.drawer_mentorDetails_label_availability1-on1',
            )}
          </Typography>
        </Box>
        <Box>
          <InlineWidget
            url={`${getCalendlyURL(
              mentor.details?.bookingUrl,
            )}&hide_event_type_details=1&hide_gdpr_banner=1&primary_color=1D386C`}
            utm={{
              utmSource: mentor.isRecommended ? recommendationId : undefined,
              utmTerm: JSON.stringify(filters),
              utmCampaign,
            }}
            styles={{
              minWidth: '320px',
              height: '630px',
              position: 'relative',
            }}
          />
        </Box>
        {mentorEvents.length > 0 && (
          <Box className={classes.eventsSection}>
            <Typography className={classes.eventsSectionTitle}>
              {t('v2.mentorOverview.drawer_mentorDetails_title_upcomingEvents')}
            </Typography>
            <Typography className={classes.mentorSubtitle}>
              {t('v2.mentorOverview.drawer_mentorDetails_label_upcomingEvents')}
            </Typography>

            <Box className={classes.mentorsContainer}>
              {mentorEvents.map((item: IBookingEvent) => {
                return (
                  <Box className={classes.hostedCardWrapper}>
                    <OfferingCard
                      upcomingEvent={item}
                      userCredit={user?.creditBalance}
                      onUpcomingEvent={onEvent}
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};

export default MentorProfile;
